import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { LocationMap } from 'src/components/map/location-map';
import type { AppointmentDTO } from 'src/types/dto/appointment.dto';
import type { InspectorDTO } from 'src/types/dto';
import { ActivityStatus } from 'src/types/dto';

interface ActivityLocationMapProps {
  readonly location: string;
  readonly siteName?: string;
  readonly activityStatus?: ActivityStatus | string;
  readonly appointments?: AppointmentDTO[];
  readonly allocatedInspectors?: InspectorDTO[];
}

export function ActivityLocationMap({
  location,
  siteName,
  activityStatus,
  appointments,
  allocatedInspectors,
}: ActivityLocationMapProps) {
  const { t } = useTranslation('activities');

  const parseLocation = (locationString: string) => {
    const [lat, lng] = locationString.split(',').map(Number);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      return { lat, lng };
    }
    return null;
  };

  const coordinates = parseLocation(location);

  const isCreatedStatus = activityStatus === ActivityStatus.CREATED;

  const markers = useMemo(() => {
    if (!coordinates) return [];

    const siteMarker = {
      position: coordinates,
      title: siteName || t('maps.markers.activity', 'Activity location'),
      draggable: false,
    };

    if (isCreatedStatus) {
      return [siteMarker];
    }

    const resourceMarkers: typeof siteMarker[] = [];
    const seenLocations = new Set<string>();

    const addResourceMarker = (inspector: InspectorDTO) => {
      if (!inspector.location) return;
      const parsed = parseLocation(inspector.location);
      if (!parsed) return;
      const key = `${parsed.lat},${parsed.lng}`;
      if (seenLocations.has(key)) return;
      seenLocations.add(key);
      resourceMarkers.push({
        position: parsed,
        title: inspector.name || 'Resource',
        draggable: false,
      });
    };

    appointments?.forEach((app) => {
      if (app.inspector) addResourceMarker(app.inspector);
    });

    allocatedInspectors?.forEach((inspector) => addResourceMarker(inspector));

    return [siteMarker, ...resourceMarkers];
  }, [coordinates, isCreatedStatus, siteName, appointments, allocatedInspectors, t]);

  if (!coordinates) {
    return (
      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          fontSize: '14px',
          textAlign: 'center',
          padding: '20px'
        }}
      >
        {t('maps.errors.invalidCoordinates', 'Invalid location coordinates')}
      </div>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        '& > div': {
          height: '100% !important',
        },
      }}
    >
      <LocationMap
        viewOnly
        hideSearchInput
        markers={markers}
        defaultLocation={location}
        defaultZoom={isCreatedStatus && markers.length === 1 ? 13 : undefined}
      />
    </Box>
  );
}
